/**
 * Tier-aware permit file handoff (capability tiers — companion §1.2 / §2.8).
 *
 * The device side is identical across tiers: render the prepared packet → hand off / send →
 * capture the result → advance the lifecycle. The correct action fires the correct backend
 * call. Tier 3/5 (portal automation) are backend/operator concerns — NOT on device (inv. 1);
 * here the device offers the always-available actions: open the AHJ URL (T0/T1) and file-by-
 * email (T4). Nothing auto-attests or bypasses a CAPTCHA (inv. 14).
 *
 * Wiring (mobile inv. 7): GET /api/permit-applications/:id (useQuery) + POST …/file/email
 * (useMutation). Loading / empty / error render. No mock data. NEVER imports @smartbox/*.
 */

import { View, StyleSheet, ActivityIndicator } from 'react-native';
import { useLocalSearchParams } from 'expo-router';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import * as WebBrowser from 'expo-web-browser';
import { getPermitApplication, fileByEmail, prepareAssistedSession, type PermitApplicationRow } from '../../../src/api/permits';
import { Screen, Text, Card, Button } from '../../../src/components/ui';
import { theme } from '../../../src/theme';
import { extractErrorMessage } from '../../../src/lib/format';

export default function PermitFileScreen() {
  const { appId } = useLocalSearchParams<{ appId: string }>();
  const queryClient = useQueryClient();

  const appQuery = useQuery({
    queryKey: ['permit-application', appId],
    queryFn: () => getPermitApplication(appId!),
    enabled: !!appId,
    staleTime: 15_000,
  });

  const emailMutation = useMutation({
    mutationFn: () => fileByEmail(appId!),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['permit-application', appId] }),
  });

  // Tier 3 — assisted-browser handoff: stage values + open the portal for HUMAN attestation.
  // The device never attests or solves a CAPTCHA; it opens the portal in a browser tab.
  const assistedMutation = useMutation({
    mutationFn: () => prepareAssistedSession(appId!),
    onSuccess: async (session) => {
      void queryClient.invalidateQueries({ queryKey: ['permit-application', appId] });
      if (session?.handoffUrl) await WebBrowser.openBrowserAsync(session.handoffUrl);
    },
  });

  if (!appId) {
    return (
      <Screen>
        <View style={styles.centeredState} testID="permit-file-screen">
          <Text variant="body" color="danger">No permit application ID provided.</Text>
        </View>
      </Screen>
    );
  }

  if (appQuery.isLoading) {
    return (
      <Screen>
        <View style={styles.centeredState} testID="permit-file-screen">
          <ActivityIndicator size="large" color={theme.color.action.primary} accessibilityLabel="Loading application" />
          <Text variant="body" color="secondary">Loading…</Text>
        </View>
      </Screen>
    );
  }

  if (appQuery.isError) {
    return (
      <Screen>
        <View style={styles.centeredState} testID="permit-file-screen">
          <Card>
            <Text variant="bodyStrong" color="danger" accessibilityRole="alert">
              {extractErrorMessage(appQuery.error, 'Failed to load application.')}
            </Text>
            <Button title="Retry" variant="secondary" size="sm" fullWidth={false} onPress={() => appQuery.refetch()} />
          </Card>
        </View>
      </Screen>
    );
  }

  const app: PermitApplicationRow | undefined = appQuery.data;
  const tier = (app as unknown as { capability_tier?: number })?.capability_tier ?? 0;

  return (
    <Screen>
      <View testID="permit-file-screen" style={styles.section}>
        <Card>
          <Text variant="bodyStrong">File this permit</Text>
          <Text variant="small" color="secondary">
            Current tier: {tier}. The app prepares and hands off — it never attests or solves a CAPTCHA.
          </Text>
        </Card>

        {/* Tier 4 — file by email from the contractor's sender identity. */}
        <Card>
          <Text variant="bodyStrong">Email the packet to the AHJ (Tier 4)</Text>
          <Text variant="small" color="secondary">
            Sends the assembled packet to the jurisdiction's intake address. Requires an email
            submission profile; otherwise this falls back to the Tier-0 packet (not an error).
          </Text>
          <Button
            title="Send packet by email"
            variant="primary"
            size="sm"
            fullWidth={false}
            loading={emailMutation.isPending}
            disabled={emailMutation.isPending}
            onPress={() => emailMutation.mutate()}
            accessibilityLabel="Assemble and email the packet to the jurisdiction intake address"
          />
          {emailMutation.isError ? (
            <Text variant="small" color="danger" accessibilityRole="alert">
              {extractErrorMessage(emailMutation.error, 'Could not send the packet by email.')}
            </Text>
          ) : null}
          {emailMutation.isSuccess ? (
            <Text variant="small" color="secondary" testID="permit-file-email-result">
              Sent — status: {emailMutation.data?.send?.status ?? 'recorded'}.
            </Text>
          ) : null}
        </Card>

        {/* Tier 3 — assisted-browser handoff (open the portal to attest). */}
        <Card>
          <Text variant="bodyStrong">Open the portal to attest (Tier 3)</Text>
          <Text variant="small" color="secondary">
            Stages your values and opens the jurisdiction's portal so you can review and submit it
            yourself. BEAM never attests or solves a CAPTCHA. Falls back if no portal adapter exists.
          </Text>
          <Button
            title="Open portal (assisted)"
            variant="secondary"
            size="sm"
            fullWidth={false}
            testID="permit-file-assisted"
            loading={assistedMutation.isPending}
            disabled={assistedMutation.isPending}
            onPress={() => assistedMutation.mutate()}
            accessibilityLabel="Stage values and open the jurisdiction portal to attest"
          />
          {assistedMutation.isError ? (
            <Text variant="small" color="danger" accessibilityRole="alert">
              {extractErrorMessage(assistedMutation.error, 'No assisted-browser portal for this jurisdiction (use email or the Tier-0 packet).')}
            </Text>
          ) : null}
        </Card>
      </View>
    </Screen>
  );
}

const styles = StyleSheet.create({
  centeredState: { flex: 1, justifyContent: 'center', alignItems: 'center', gap: theme.spacing.md, paddingHorizontal: theme.spacing['2xl'] },
  section: { gap: theme.spacing.sm },
});
